import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Map as MapIcon,
  PauseCircleOutline as PauseIcon,
  PlayCircleOutline as ResumeIcon,
  SignalWifi4Bar as SignalIcon,
  ViewModule as CardsIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useProbeMetrics } from '../../hooks/useProbeMetrics';
import MonitorWorldMap from './MonitorWorldMap';
import MonitorRegionCards from './MonitorRegionCards';

interface MonitoringPanelProps {
  tenantId: string;
  scenarioId: string;
}

type ViewMode = 'map' | 'cards';

const MonitoringPanel: React.FC<MonitoringPanelProps> = ({ tenantId, scenarioId }) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('cards');

  // Target management state
  const [scenarioTargets, setScenarioTargets] = useState<string[]>([]);
  const [extraTargets, setExtraTargets] = useState<string[]>([]);
  const [targetConfigLoaded, setTargetConfigLoaded] = useState(false);
  const [hiddenScenarioTargets, setHiddenScenarioTargets] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('monitor_hidden_scenario_targets');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const [paused, setPaused] = useState(false);
  const [addInput, setAddInput] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Provisioned regions from the active monitor unit
  const [provisionedRegions, setProvisionedRegions] = useState<string[]>([]);

  const { targets: rawTargets, regions: rawRegions, loading, error } = useProbeMetrics(tenantId, scenarioId, 5_000, paused);

  // Filter out stale Prometheus data for targets that were removed.
  const visibleScenarioTargets = scenarioTargets.filter((t) => !hiddenScenarioTargets.has(t));
  const knownTargets = new Set([...visibleScenarioTargets, ...extraTargets]);
  const targets = targetConfigLoaded
    ? rawTargets.filter((t) => knownTargets.has(t.target))
    : rawTargets;
  const regions = targetConfigLoaded
    ? rawRegions.filter((r) => knownTargets.has(r.target))
    : rawRegions;

  // All configured targets in display order (for region card seeding)
  const configuredTargets = [...visibleScenarioTargets, ...extraTargets];

  const totalUp    = targets.reduce((s, t) => s + t.upCount, 0);
  const totalCount = targets.reduce((s, t) => s + t.totalCount, 0);
  const avgMs =
    targets.length > 0
      ? Math.round(targets.reduce((s, t) => s + (t.avgMs ?? 0), 0) / targets.length)
      : null;

  const lastCheckedAt: string | null = (() => {
    const allTs = targets.flatMap((t) => t.regions).map((r) => r.checkedAt).filter(Boolean);
    if (allTs.length === 0) return null;
    const latest = allTs.reduce((a, b) => (a > b ? a : b));
    return new Date(latest).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  })();

  const statusColor =
    totalCount === 0
      ? 'default'
      : totalUp === totalCount
      ? 'success'
      : totalUp === 0
      ? 'error'
      : 'warning';

  // Load saved targets + paused state, and fetch the active monitor unit's provisioned regions
  useEffect(() => {
    let active = true;
    setTargetConfigLoaded(false);
    setScenarioTargets([]);
    setExtraTargets([]);

    // Fetch monitor targets config
    api.getMonitorTargets()
      .then((data) => {
        if (!active) return;
        setScenarioTargets(data.scenario_targets);
        setExtraTargets(data.monitor_targets);
        setPaused(data.paused ?? false);
      })
      .catch(() => { /* no running execution — silent */ })
      .finally(() => { if (active) setTargetConfigLoaded(true); });

    // Fetch provisioned regions from the active monitor unit
    api.getActiveMonitorRegions()
      .then((unit) => {
        if (!active) return;
        setProvisionedRegions(unit?.provisioned_regions ?? []);
      })
      .catch(() => { /* no monitor unit configured */ });

    return () => { active = false; };
  }, [scenarioId]);

  const persistTargets = async (next: string[]) => {
    setSaving(true);
    try {
      const data = await api.setMonitorTargets(next);
      setExtraTargets(data.monitor_targets);
      setScenarioTargets(data.scenario_targets);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePause = async () => {
    const next = !paused;
    setPaused(next);
    setSaving(true);
    try {
      const data = await api.setMonitorPaused(next);
      setPaused(data.paused);
    } catch {
      setPaused(!next);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleScenarioTarget = (url: string) => {
    setHiddenScenarioTargets((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      try { localStorage.setItem('monitor_hidden_scenario_targets', JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  const handleAddTarget = async () => {
    const url = addInput.trim();
    if (!url) return;
    if (!/^https?:\/\/.+/.test(url)) {
      setAddError('Must be a valid http:// or https:// URL');
      return;
    }
    if (scenarioTargets.includes(url) || extraTargets.includes(url)) {
      setAddError('Target already monitored');
      return;
    }
    setAddError(null);
    setAddInput('');
    // Optimistic update — region cards show loading immediately for the new target
    const next = [...extraTargets, url];
    setExtraTargets(next);
    await persistTargets(next);
  };

  const handleRemoveExtra = async (url: string) => {
    const next = extraTargets.filter((t) => t !== url);
    setExtraTargets(next);
    await persistTargets(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAddTarget();
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: theme.palette.divider,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Availability Monitoring
          </Typography>

          {totalCount > 0 && (
            <>
              <Chip
                size="small"
                icon={<SignalIcon sx={{ fontSize: '0.85rem !important' }} />}
                label={`${totalUp}/${totalCount} up`}
                color={statusColor as any}
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
              {avgMs !== null && (
                <Chip
                  size="small"
                  label={`~${avgMs} ms avg`}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem', borderColor: theme.palette.divider }}
                />
              )}
              {lastCheckedAt && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                >
                  checked {lastCheckedAt}
                </Typography>
              )}
              {paused ? (
                <Chip size="small" label="PAUSED" color="warning" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700 }} />
              ) : (
                <Chip size="small" label="LIVE" color="success" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700 }} />
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {(loading || saving) && <CircularProgress size={16} sx={{ mr: 0.5 }} />}

          <Tooltip title={paused ? 'Resume monitoring' : 'Pause monitoring'}>
            <IconButton
              size="small"
              onClick={handleTogglePause}
              disabled={saving}
              sx={{
                color: paused ? 'warning.main' : 'text.secondary',
                '&:hover': { color: paused ? 'warning.dark' : 'text.primary' },
              }}
            >
              {paused
                ? <ResumeIcon sx={{ fontSize: 20 }} />
                : <PauseIcon  sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, val) => val && setViewMode(val)}
          >
            <ToggleButton value="map" aria-label="World map">
              <Tooltip title="World map"><MapIcon sx={{ fontSize: 16 }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value="cards" aria-label="Region cards">
              <Tooltip title="Region cards"><CardsIcon sx={{ fontSize: 16 }} /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ── Target toolbar ── */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', mr: 0.5, whiteSpace: 'nowrap', fontWeight: 600 }}>
          Targets:
        </Typography>

        {/* Scenario targets — hideable */}
        {scenarioTargets.map((t) => {
          const hidden = hiddenScenarioTargets.has(t);
          return (
            <Chip
              key={t}
              label={t}
              variant="filled"
              onDelete={() => handleToggleScenarioTarget(t)}
              deleteIcon={
                <Tooltip title={hidden ? 'Show target' : 'Hide target'}>
                  {hidden
                    ? <ShowIcon sx={{ fontSize: '1rem !important', opacity: 0.7 }} />
                    : <HideIcon sx={{ fontSize: '1rem !important', opacity: 0.7 }} />}
                </Tooltip>
              }
              sx={{
                height: 28,
                fontSize: '0.8rem',
                maxWidth: 320,
                bgcolor: hidden
                  ? alpha(theme.palette.action.disabled, 0.08)
                  : alpha(theme.palette.primary.main, 0.12),
                color: hidden ? 'text.disabled' : theme.palette.primary.main,
                opacity: hidden ? 0.55 : 1,
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          );
        })}

        {/* Extra (user-added) targets */}
        {extraTargets.map((t) => (
          <Chip
            key={t}
            label={t}
            variant="outlined"
            onDelete={() => handleRemoveExtra(t)}
            deleteIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
            sx={{
              height: 28,
              fontSize: '0.8rem',
              maxWidth: 320,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ))}

        {/* Add target input */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
          <TextField
            inputRef={inputRef}
            value={addInput}
            onChange={(e) => { setAddInput(e.target.value); setAddError(null); }}
            onKeyDown={handleKeyDown}
            placeholder="https://example.com"
            size="small"
            error={!!addError}
            title={addError ?? undefined}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={addError ?? 'Add target'}>
                    <span>
                      <IconButton size="small" onClick={handleAddTarget} disabled={!addInput.trim()}>
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              '& .MuiInputBase-root': { height: 32, fontSize: '0.85rem' },
            }}
          />
        </Box>
      </Box>

      {/* ── Body ── */}
      <CardContent sx={{ p: '0 !important' }}>
        {error && (
          <Alert severity="warning" sx={{ m: 1.5 }}>{error}</Alert>
        )}

        {/* Both views stay mounted so MonitorRegionCards keeps its history buffer across view switches */}
        <Box sx={{ display: viewMode === 'map' ? 'block' : 'none', height: 520 }}>
          <MonitorWorldMap regions={regions} />
        </Box>
        <Box sx={{ display: viewMode === 'cards' ? 'block' : 'none' }}>
          <MonitorRegionCards
            targets={targets}
            loading={loading}
            provisionedRegions={provisionedRegions}
            configuredTargets={configuredTargets}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonitoringPanel;
